import React, { useState, useMemo } from "react"
import { InputAdornment, TextField, IconButton, Paper } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import ClearIcon from "@mui/icons-material/Clear"
import { COLORS } from "../utils"

/**
 * Reusable SearchBar with Autocomplete dropdown
 * 
 * @param {string} placeholder - Placeholder text for the input
 * @param {Array} options - Array of options for autocomplete, each with { label, value, ...extraData }
 * @param {function} onSearch - Callback when search query changes, receives (searchQuery)
 * @param {function} onSelect - Callback when an option is selected, receives (selectedOption)
 * @param {function} customFilter - Optional custom filter function (option, query) => boolean
 * @param {object} sx - Optional additional styles for the container
 */
const SearchBarWithAutocomplete = ({
  placeholder = "Rechercher...",
  options = [],
  onSearch,
  onSelect,
  customFilter,
  sx = {},
}) => {
  const [inputValue, setInputValue] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    const query = inputValue.toLowerCase().trim()
    if (!query) return []
    // Custom filter function
    if (customFilter) return options.filter((option) => customFilter(option, query))
    // Default filter: match label
    return options.filter((option) => option.label.toLowerCase().includes(query))
  }, [options, inputValue, customFilter])

  const handleInputChange = (value) => {
    setInputValue(value)
    setShowDropdown(value.trim().length > 0)

    if (onSearch) onSearch(value)
  }

  const handleClear = () => {
    setInputValue("")
    setShowDropdown(false)

    if (onSearch) onSearch("")
  }

  const handleSelect = (option) => {
    setInputValue(option.label)
    setShowDropdown(false)

    if (onSelect) onSelect(option)
  }

  return (
    <div style={{ ...styles.container, ...sx }}>
      <TextField
        fullWidth
        size="small"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => { if (inputValue.trim().length > 0) setShowDropdown(true) }}
        onKeyDown={(e) => { if (e.key === "Enter" && inputValue.trim().length > 0) setShowDropdown(false) }}
        sx={styles.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: COLORS.BLACK_600 }} />
            </InputAdornment>
          ),
          endAdornment: inputValue && (
            <InputAdornment position="end">
              <IconButton
                sx={styles.adornmentClearButton}
                onClick={handleClear}
                edge="end"
              >
                <ClearIcon fontSize="regular" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      {/* Autocomplete dropdown */}
      {showDropdown && filteredOptions.length > 0 && (
        <Paper sx={styles.autocompletePaper}>
          <div style={{ maxHeight: "273px", overflowY: "auto" }}>
            {filteredOptions.map((option, index) => (
              <div
                key={option.key || index}
                style={styles.autocompleteOption}
                onClick={() => handleSelect(option)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = COLORS.BACKGROUND_LIGHT_GREY
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent"
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        </Paper>
      )}
    </div>
  )
}

export default SearchBarWithAutocomplete

const styles = {
  container: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    background: COLORS.WHITE,
    borderBottom: "1px solid" + COLORS.BACKGROUND_LIGHT_GREY,
  },
  searchField: {
    backgroundColor: COLORS.BACKGROUND_LIGHT_GREY,
    borderRadius: "8px",
    "& .MuiInputBase-input": {
      padding: "0",
      fontSize: "16px",
      lineHeight: "24px",
      color: COLORS.BLACK800,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    "& .MuiInputBase-formControl": {
      padding: "12px !important",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "1px solid" + COLORS.BLACK_200,
      },
      "&.Mui-focused fieldset": {
        border: "2px solid" + COLORS.BLACK800,
      },
    },
  },
  autocompletePaper: {
    borderRadius: "4px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
    backgroundColor: COLORS.WHITE,
  },
  autocompleteOption: {
    padding: "8px",
    marginTop: "8px",
    fontSize: "16px",
    lineHeight: "24px",
    color: COLORS.BLACK800,
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  adornmentClearButton: { 
    color: COLORS.BLACK800, 
    padding: 0, 
    width: "24px",
    height: "24px",
    margin: 0
  }
}
